// src/pages/Permissions.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import { RoleBadge } from '../components/Badge';
import {
    ShieldCheckIcon,
    UserGroupIcon,
    CogIcon,
    DocumentTextIcon,
    ChevronDownIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';

const Permissions = () => {
    const { executeQuery, queries, loading, error } = useGraphQL();
    const [permissions, setPermissions] = useState([]);
    const [rolePermissions, setRolePermissions] = useState([]);
    const [selectedRole, setSelectedRole] = useState('USER');
    const [selectedModule, setSelectedModule] = useState('');
    const [expandedModules, setExpandedModules] = useState(new Set());
    const [view, setView] = useState('permissions'); // 'permissions' | 'roles'

    const roles = ['ADMIN', 'USER', 'COMPANY_ADMIN', 'COMPANY_USER'];

    useEffect(() => {
        loadPermissions();
    }, [selectedModule]);

    useEffect(() => {
        if (view === 'roles' && selectedRole) {
            loadRolePermissions();
        }
    }, [selectedRole, view]);

    const loadPermissions = async () => {
        try {
            const data = await executeQuery(queries.getPermissions(), {
                module: selectedModule || undefined
            });
            setPermissions(data.permissions || []);
        } catch (err) {
            console.error('Failed to load permissions:', err);
        }
    };

    const loadRolePermissions = async () => {
        try {
            const data = await executeQuery(queries.getRolePermissions(), {
                role: selectedRole
            });
            setRolePermissions(data.rolePermissions || []);
        } catch (err) {
            console.error('Failed to load role permissions:', err);
        }
    };

    // Group permissions by module
    const permissionsByModule = permissions.reduce((acc, permission) => {
        const module = permission.module || 'General';
        if (!acc[module]) {
            acc[module] = [];
        }
        acc[module].push(permission);
        return acc;
    }, {});

    const toggleModule = (module) => {
        const newExpanded = new Set(expandedModules);
        if (newExpanded.has(module)) {
            newExpanded.delete(module);
        } else {
            newExpanded.add(module);
        }
        setExpandedModules(newExpanded);
    };

    const getModuleIcon = (module) => {
        switch (module.toLowerCase()) {
            case 'users':
            case 'user':
                return <UserGroupIcon className="h-4 w-4" />;
            case 'companies':
            case 'company':
                return <DocumentTextIcon className="h-4 w-4" />;
            case 'tenders':
            case 'tender':
                return <DocumentTextIcon className="h-4 w-4" />;
            case 'system':
            case 'admin':
                return <CogIcon className="h-4 w-4" />;
            default:
                return <ShieldCheckIcon className="h-4 w-4" />;
        }
    };

    // eslint-disable-next-line no-unused-vars
    const permissionsColumns = [
        {
            key: 'name',
            title: 'Permission',
            sortable: true,
            render: (name, permission) => (
                <div>
                    <div className="font-medium text-white">{name}</div>
                    <div className="text-sm text-gray-400">{permission.description}</div>
                </div>
            )
        },
        {
            key: 'module',
            title: 'Module',
            sortable: true,
            render: (module) => (
                <div className="flex items-center">
                    <div className="text-gray-400 mr-2">
                        {getModuleIcon(module)}
                    </div>
                    <span className="text-white">{module || 'General'}</span>
                </div>
            )
        },
        {
            key: 'createdAt',
            title: 'Created',
            sortable: true,
            render: (date) => new Date(date).toLocaleDateString()
        }
    ];

    // eslint-disable-next-line no-unused-vars
    const rolePermissionsColumns = [
        {
            key: 'permission',
            title: 'Permission',
            render: (_, rolePermission) => (
                <div>
                    <div className="font-medium text-white">{rolePermission.permission?.name}</div>
                    <div className="text-sm text-gray-400">{rolePermission.permission?.description}</div>
                </div>
            )
        },
        {
            key: 'module',
            title: 'Module',
            render: (_, rolePermission) => (
                <div className="flex items-center">
                    <div className="text-gray-400 mr-2">
                        {getModuleIcon(rolePermission.permission?.module)}
                    </div>
                    <span className="text-white">{rolePermission.permission?.module || 'General'}</span>
                </div>
            )
        },
        {
            key: 'grantedAt',
            title: 'Granted',
            render: (_, rolePermission) => new Date(rolePermission.createdAt).toLocaleDateString()
        }
    ];

    const uniqueModules = [...new Set(permissions.map(p => p.module).filter(Boolean))];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-white">Permissions</h1>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setView('permissions')}
                        className={`px-4 py-2 ${
 view === 'permissions'
 ? 'bg-primary text-white'
 : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
 }`}
                    >
                        All Permissions
                    </button>
                    <button
                        onClick={() => setView('roles')}
                        className={`px-4 py-2 ${
 view === 'roles'
 ? 'bg-primary text-white'
 : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
 }`}
                    >
                        Role Permissions
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-primary p-2 mr-3">
                            <ShieldCheckIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Total Permissions</p>
                            <p className="text-xl font-semibold text-white">{permissions.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-accent-blue p-2 mr-3">
                            <CogIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Modules</p>
                            <p className="text-xl font-semibold text-white">{uniqueModules.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-accent-green p-2 mr-3">
                            <UserGroupIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Roles</p>
                            <p className="text-xl font-semibold text-white">{roles.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-secondary p-2 mr-3">
                            <ShieldCheckIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Role Permissions</p>
                            <p className="text-xl font-semibold text-white">{rolePermissions.length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {view === 'permissions' && (
                <>
                    {/* Filters */}
                    <div className="bg-gray-800 p-4">
                        <div className="flex items-center space-x-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Module</label>
                                <select
                                    value={selectedModule}
                                    onChange={(e) => setSelectedModule(e.target.value)}
                                    className="bg-gray-700 border border-gray-600 text-white p-2"
                                >
                                    <option value="">All Modules</option>
                                    {uniqueModules.map(module => (
                                        <option key={module} value={module}>{module}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={() => setSelectedModule('')}
                                    className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-500"
                                >
                                    Clear Filter
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 p-4">
                            <p className="text-red-700">Error: {error}</p>
                        </div>
                    )}

                    {/* Permissions by Module */}
                    <div className="space-y-4">
                        {Object.entries(permissionsByModule).map(([module, modulePermissions]) => (
                            <div key={module} className="bg-gray-800 overflow-hidden">
                                <button
                                    onClick={() => toggleModule(module)}
                                    className="w-full px-6 py-4 flex items-center justify-between bg-gray-700 hover:bg-gray-600 transition-colors"
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="text-gray-400">
                                            {getModuleIcon(module)}
                                        </div>
                                        <h3 className="text-lg font-medium text-white">{module}</h3>
                                        <span className="bg-gray-600 text-gray-300 px-2 py-1 text-sm">
                                            {modulePermissions.length} permissions
                                        </span>
                                    </div>
                                    <div className="text-gray-400">
                                        {expandedModules.has(module) ?
                                            <ChevronDownIcon className="h-5 w-5" /> :
                                            <ChevronRightIcon className="h-5 w-5" />
                                        }
                                    </div>
                                </button>

                                {expandedModules.has(module) && (
                                    <div className="px-6 py-4">
                                        <div className="space-y-3">
                                            {modulePermissions.map((permission) => (
                                                <div key={permission.id} className="border-l-2 border-primary pl-4">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <h4 className="font-medium text-white">{permission.name}</h4>
                                                            <p className="text-sm text-gray-400 mt-1">{permission.description}</p>
                                                        </div>
                                                        <span className="text-xs text-gray-500 font-mono">
                                                            {permission.id}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {view === 'roles' && (
                <>
                    {/* Role Selector */}
                    <div className="bg-gray-800 p-4">
                        <div className="flex items-center space-x-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
                                <select
                                    value={selectedRole}
                                    onChange={(e) => setSelectedRole(e.target.value)}
                                    className="bg-gray-700 border border-gray-600 text-white p-2"
                                >
                                    {roles.map(role => (
                                        <option key={role} value={role}>{role}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-end">
                                <RoleBadge role={selectedRole} />
                            </div>
                        </div>
                    </div>

                    {/* Role Permission Summary */}
                    <div className="bg-gray-800 p-6">
                        <h3 className="text-lg font-medium text-white mb-4">
                            Permissions for <RoleBadge role={selectedRole} />
                        </h3>

                        {loading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {rolePermissions.length === 0 ? (
                                    <div className="text-center text-gray-400 py-8">
                                        No permissions assigned to this role
                                    </div>
                                ) : (
                                    <>
                                        {/* Group role permissions by module */}
                                        {Object.entries(
                                            rolePermissions.reduce((acc, rp) => {
                                                const module = rp.permission?.module || 'General';
                                                if (!acc[module]) acc[module] = [];
                                                acc[module].push(rp);
                                                return acc;
                                            }, {})
                                        ).map(([module, moduleRolePermissions]) => (
                                            <div key={module} className="border border-gray-700 p-4">
                                                <div className="flex items-center space-x-2 mb-3">
                                                    <div className="text-gray-400">
                                                        {getModuleIcon(module)}
                                                    </div>
                                                    <h4 className="font-medium text-white">{module}</h4>
                                                    <span className="bg-gray-600 text-gray-300 px-2 py-1 text-xs">
                                                        {moduleRolePermissions.length} permissions
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    {moduleRolePermissions.map((rp) => (
                                                        <div key={rp.id} className="bg-gray-700 p-3">
                                                            <div className="text-sm text-white font-medium">
                                                                {rp.permission?.name}
                                                            </div>
                                                            <div className="text-xs text-gray-400 mt-1">
                                                                {rp.permission?.description}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Permissions;